import React, { useState, useEffect } from 'react';
import { FaSync, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import api from '../../services/api';
import SyncProgress from '../../components/Onboarding/SyncProgress';

const Step4FirstSync = ({ onNext, onBack, spApiConfigured }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    products: { status: 'idle', progress: 0 },
    orders: { status: 'idle', progress: 0 }
  });
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  const startSync = async () => {
    if (!spApiConfigured) {
      setError('Please configure SP-API credentials first');
      return;
    }

    setSyncing(true);
    setError('');

    try {
      // Start product sync
      setSyncStatus(prev => ({
        ...prev,
        products: { status: 'running', progress: 0 }
      }));
      
      await api.post('/sync/products');
      
      // Poll for product sync status
      const productInterval = setInterval(async () => {
        try {
          const { data } = await api.get('/sync/status');
          const productStatus = data.data.products;
          
          setSyncStatus(prev => ({
            ...prev,
            products: {
              status: productStatus.status,
              progress: productStatus.progress || 0
            }
          }));

          if (productStatus.status === 'completed') {
            clearInterval(productInterval);
            // Start order sync
            startOrderSync();
          }
        } catch (err) {
          clearInterval(productInterval);
          setError('Failed to sync products');
          setSyncing(false);
        }
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start sync');
      setSyncing(false);
    }
  };

  const startOrderSync = async () => {
    try {
      setSyncStatus(prev => ({
        ...prev,
        orders: { status: 'running', progress: 0 }
      }));
      
      await api.post('/sync/orders');
      
      // Poll for order sync status
      const orderInterval = setInterval(async () => {
        try {
          const { data } = await api.get('/sync/status');
          const orderStatus = data.data.orders;
          
          setSyncStatus(prev => ({
            ...prev,
            orders: {
              status: orderStatus.status,
              progress: orderStatus.progress || 0
            }
          }));

          if (orderStatus.status === 'completed') {
            clearInterval(orderInterval);
            setSyncing(false);
            setCompleted(true);
          }
        } catch (err) {
          clearInterval(orderInterval);
          setError('Failed to sync orders');
          setSyncing(false);
        }
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start order sync');
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <FaSync className="text-3xl text-blue-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Import Your Data
        </h2>
        <p className="text-gray-600">
          Let's sync your products and orders from Amazon
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
          <div className="flex items-center">
            <FaExclamationCircle className="text-red-500 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {completed ? (
        <div className="text-center py-12">
          <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">
            Sync Completed Successfully!
          </h3>
          <p className="text-gray-600 mb-6">
            Your products and orders have been imported
          </p>
          <button
            onClick={() => onNext({ firstSyncCompleted: true })}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
          >
            Continue to Tour →
          </button>
        </div>
      ) : (
        <>
          <SyncProgress
            productsStatus={syncStatus.products}
            ordersStatus={syncStatus.orders}
          />

          <div className="mt-8 flex justify-between">
            <button
              onClick={onBack}
              disabled={syncing}
              className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50"
            >
              ← Back
            </button>
            {!syncing && !completed && (
              <button
                onClick={startSync}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
              >
                Start Sync
              </button>
            )}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">
              What happens during sync?
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• We'll import all your active products from Amazon</li>
              <li>• Order history will be synced (90 days for Starter, unlimited for Pro)</li>
              <li>• This process usually takes 2-5 minutes</li>
              <li>• Future syncs will happen automatically based on your plan</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default Step4FirstSync;
